/**
 * NASDAQ Quantum — etiquetas de eje de Entrada / TP / SL ancladas a su precio.
 *
 * Antes eran `addPriceLine` nativas: su etiqueta entra en el anti-solape
 * global del eje de lightweight-charts (`alignLabels`), que separa TODAS las
 * etiquetas empujándolas hacia fuera desde el centro del pane, sin prioridad
 * y apilando en columna (bug 2026-09-28: TP ~100px y Entrada ~80px lejos de
 * su nivel, detrás de una pila de títulos "NQ Rango" históricos).
 *
 * Aquí las tres etiquetas se colocan ANTES de que el eje las vea: cada una
 * en la coordenada de su precio, recalculada en cada repaint
 * (`updateAllViews`, Standard C.2), y si colisionan entre ellas se desplaza
 * la de menor prioridad lo mínimo (Entrada > TP > SL) hasta quedar libre --
 * nunca más lejos de un alto de etiqueta por cada etiqueta con la que choca.
 * Las demás etiquetas del eje cuya posición se conoce (precio vivo de la
 * serie de velas, EMA y rango vigente del propio indicador) cuentan como
 * obstáculos fijos: así el anti-solape nativo no encuentra ningún solape que
 * resolver y no desplaza ninguna de las tres (empuja desde el centro del
 * pane hacia fuera, sin prioridad).
 *
 * Mismo aspecto que la priceLine nativa: precio en el eje (formateado por la
 * serie de velas, como el eje nativo) y título en una pastilla del mismo
 * color pegada al eje, dentro del pane.
 */

// Métricas del renderer de etiquetas de eje de lightweight-charts v4
// (fuente 12px → padding vertical 2.5px + 2px extra que el wrapper de
// primitivas añade por lado; padding horizontal = tickLength 5px).
const PADDING_V_PER_12PX = 2.5 + 2
const TICK_LENGTH = 5
// Margen entre etiquetas: el eje redondea a píxel de bitmap, y separarlas
// exactamente un alto deja solapes subpíxel que reactivan el anti-solape
// nativo (visto en las capturas del arnés: SL a 20.4px del rango vigente).
const LABEL_GAP = 2

export function labelHeightFor(fontSize) {
  return Math.ceil(fontSize + (2 * PADDING_V_PER_12PX * fontSize) / 12)
}

/**
 * Colocación con prioridad. `items` en orden de prioridad (el primero nunca
 * se mueve salvo que choque con un obstáculo), cada uno `{ key, y, preferDir }`
 * con `y` = coordenada real de su precio y `preferDir` (-1 arriba / +1 abajo)
 * = lado hacia el que ceder si dos huecos libres están a la misma distancia.
 * `fixed` = coordenadas de etiquetas ajenas que no se pueden mover.
 * Devuelve `{ key: y }`. Pura: sin chart, testeable.
 */
export function resolveLevelLayout(items, labelHeight, fixed = []) {
  const placed = fixed.filter(Number.isFinite)
  const out = {}
  const step = labelHeight + LABEL_GAP
  const collides = (y) => placed.some(p => Math.abs(p - y) < step - 1e-9)
  for (const it of items) {
    if (!Number.isFinite(it.y)) continue
    let y = it.y
    if (collides(y)) {
      const candidates = []
      for (const p of placed) candidates.push(p - step, p + step)
      let best = null
      for (const c of candidates) {
        if (collides(c)) continue
        const d = Math.abs(c - it.y)
        const onPreferred = Math.sign(c - it.y) === (it.preferDir || 0)
        if (best === null || d < best.d - 1e-9 || (Math.abs(d - best.d) <= 1e-9 && onPreferred)) {
          best = { y: c, d }
        }
      }
      if (best) y = best.y
    }
    placed.push(y)
    out[it.key] = y
  }
  return out
}

// Contraste de texto sobre el fondo del color del nivel -- misma fórmula que
// generateContrastColors de lightweight-charts (no exportada).
function parseColor(c) {
  if (typeof c !== 'string') return null
  const s = c.trim()
  let m = /^#([0-9a-f]{3})$/i.exec(s)
  if (m) return m[1].split('').map(h => parseInt(h + h, 16))
  m = /^#([0-9a-f]{6})([0-9a-f]{2})?$/i.exec(s)
  if (m) return [0, 2, 4].map(i => parseInt(m[1].slice(i, i + 2), 16))
  m = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i.exec(s)
  if (m) return [Number(m[1]), Number(m[2]), Number(m[3])]
  return null
}

export function contrastTextColor(bg) {
  const rgb = parseColor(bg)
  if (!rgb) return 'white'
  const gamma = 0.199 * rgb[0] + 0.687 * rgb[1] + 0.114 * rgb[2]
  return gamma < 160 ? 'white' : 'black'
}

class TitlePaneRenderer {
  constructor(source) {
    this._source = source
  }

  draw(target) {
    const { labels, fontSize, fontFamily, labelHeight } = this._source
    if (!labels.length) return
    target.useMediaCoordinateSpace(({ context: ctx, mediaSize }) => {
      ctx.save()
      ctx.font = `${fontSize}px ${fontFamily}`
      ctx.textBaseline = 'middle'
      ctx.textAlign = 'right'
      const pad = (fontSize / 12) * TICK_LENGTH
      for (const l of labels) {
        if (!l.visible || !l.title) continue
        const w = Math.ceil(ctx.measureText(l.title).width) + 2 * pad
        const x = mediaSize.width - w
        const top = Math.round(l.y - labelHeight / 2)
        ctx.fillStyle = l.color
        ctx.beginPath()
        if (ctx.roundRect) ctx.roundRect(x, top, w, labelHeight, [2, 0, 0, 2])
        else ctx.rect(x, top, w, labelHeight)
        ctx.fill()
        ctx.fillStyle = l.textColor
        ctx.fillText(l.title, mediaSize.width - pad, top + labelHeight / 2)
      }
      ctx.restore()
    })
  }
}

class TitlePaneView {
  constructor(source) {
    this._renderer = new TitlePaneRenderer(source)
  }

  zOrder() {
    return 'top'
  }

  renderer() {
    return this._renderer
  }
}

class LevelAxisView {
  constructor(label) {
    this._l = label
  }

  coordinate() { return this._l.y }
  // Obligatorio en lightweight-charts 4.2: la vista de eje de una primitiva
  // re-lee fixedCoordinate() en CADA coordinate()/isVisible() (su cache no
  // se invalida nunca), pisando el valor que acaba de fijar alignLabels. Sin
  // este método queda `undefined` → el anti-solape la ve en y=0 y empuja
  // hacia arriba al resto del eje (el precio vivo acababa en el borde
  // superior). Con él, la etiqueta queda fija en su posición resuelta.
  fixedCoordinate() { return this._l.y }
  text() { return this._l.axisText }
  textColor() { return this._l.textColor }
  backColor() { return this._l.color }
  visible() { return this._l.visible }
}

/**
 * @param {Array<{key: string, price: number, title: string, color: string, preferDir: number}>} levels
 *   en orden de prioridad (Entrada, TP, SL).
 * @param {{priceSeries: object}} opts  serie de velas: su priceToCoordinate y
 *   su formateador de precio son los del eje donde viven las etiquetas.
 */
export class LevelLabelsPrimitive {
  constructor(levels, { priceSeries, obstaclePrices = [] }) {
    this._levels = levels.filter(l => Number.isFinite(l.price))
    this._priceSeries = priceSeries
    this._obstaclePrices = obstaclePrices.filter(Number.isFinite)
    this._chart = null
    this.labels = []
    this.fontSize = 12
    this.fontFamily = 'sans-serif'
    this.labelHeight = labelHeightFor(12)
    this._paneViews = [new TitlePaneView(this)]
    this._axisViews = []
  }

  attached({ chart }) {
    this._chart = chart
  }

  detached() {
    this._chart = null
  }

  updateAllViews() {
    const layout = this._chart?.options?.()?.layout
    this.fontSize = Number.isFinite(layout?.fontSize) ? layout.fontSize : 12
    this.fontFamily = layout?.fontFamily || 'sans-serif'
    this.labelHeight = labelHeightFor(this.fontSize)
    const paneHeight = this._chart?.paneSize?.()?.height
    const series = this._priceSeries
    const fmt = series?.priceFormatter?.()

    const raw = this._levels.map(l => {
      const y = series?.priceToCoordinate?.(l.price)
      return { ...l, y: Number.isFinite(y) ? y : null }
    })
    const fixed = this._obstaclePrices.map(p => series?.priceToCoordinate?.(p))
    const live = this._livePrice()
    if (Number.isFinite(live)) fixed.push(series.priceToCoordinate(live))
    const layoutY = resolveLevelLayout(
      raw.map(l => ({ key: l.key, y: l.y, preferDir: l.preferDir })),
      this.labelHeight,
      fixed.filter(Number.isFinite),
    )

    this.labels = raw.map(l => {
      const y = layoutY[l.key]
      // Nivel fuera del pane: se oculta, igual que la priceLine nativa.
      const inPane = Number.isFinite(y) && (!Number.isFinite(paneHeight) || (y >= 0 && y <= paneHeight))
      return {
        key: l.key,
        y: Number.isFinite(y) ? y : 0,
        visible: inPane,
        title: l.title,
        color: l.color,
        textColor: contrastTextColor(l.color),
        axisText: fmt ? fmt.format(l.price) : String(l.price),
      }
    })
    this._axisViews = this.labels.map(l => new LevelAxisView(l))
  }

  // Precio de la última vela (se mueve con cada tick en vivo): se lee en cada
  // repaint, igual que el eje nativo. Solo si la serie muestra su etiqueta.
  _livePrice() {
    const series = this._priceSeries
    if (series?.options?.()?.lastValueVisible === false) return null
    // dataByIndex y no data(): data() copia TODAS las velas en cada llamada.
    // -1 = MismatchDirection.NearestLeft → la última vela existente.
    const last = typeof series?.dataByIndex === 'function' ? series.dataByIndex(Number.MAX_SAFE_INTEGER, -1) : null
    const v = last && (Number.isFinite(last.close) ? last.close : last.value)
    return Number.isFinite(v) ? v : null
  }

  priceAxisViews() {
    return this._axisViews
  }

  paneViews() {
    return this._paneViews
  }
}
